import React, { useState, useRef } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  maxLength?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  label,
  required = false,
  error,
  maxLength = 5000
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Text formatting functions
  const insertTextAtCursor = (beforeText: string, afterText: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    const newText = value.substring(0, start) + beforeText + selectedText + afterText + value.substring(end);
    onChange(newText);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + beforeText.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Convert HTML to markdown
  const convertHtmlToMarkdown = (html: string): string => {
    return html
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')  
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
      .replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n\n')
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
      .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
      .replace(/<ul[^>]*>(.*?)<\/ul>/gis, '$1\n')
      .replace(/<ol[^>]*>(.*?)<\/ol>/gis, '$1\n')
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n')
      .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
      .replace(/<img[^>]*src="([^"]*)"[^>]*alt="([^"]*)"[^>]*>/gi, '![alt]($1)')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
      .replace(/<div[^>]*>(.*?)<\/div>/gis, '$1\n')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\n\s*\n\s*\n/g, '\n\n') // Reduce 3+ newlines to 2
      .trim();
  };

  // HTML paste feature
  const pasteHtmlContent = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      if (clipboardText.includes('<') && clipboardText.includes('>')) {
        // If it looks like HTML, convert to markdown
        const markdown = convertHtmlToMarkdown(clipboardText);
        onChange(value + '\n\n' + markdown);
      } else {
        // Plain text, just append
        onChange(value + '\n\n' + clipboardText);
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      alert('Failed to read clipboard. Please paste manually.');
    }
  };

  const formatButtons = [
    { name: 'Heading 1', action: () => insertTextAtCursor('# ', '\n'), icon: 'H1' },
    { name: 'Heading 2', action: () => insertTextAtCursor('## ', '\n'), icon: 'H2' },
    { name: 'Heading 3', action: () => insertTextAtCursor('### ', '\n'), icon: 'H3' },
    { name: 'Bold', action: () => insertTextAtCursor('**', '**'), icon: 'B' },
    { name: 'Italic', action: () => insertTextAtCursor('*', '*'), icon: 'I' },
    { name: 'Bullet List', action: () => insertTextAtCursor('- ', '\n'), icon: '•' },
    { name: 'Numbered List', action: () => insertTextAtCursor('1. ', '\n'), icon: '1.' },
    { name: 'Link', action: () => insertTextAtCursor('[', '](url)'), icon: 'Link' },
    { name: 'Paste HTML', action: pasteHtmlContent, icon: 'HTML' },
  ];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl+B: Bold
    if (e.ctrlKey && e.key === 'b') {
      e.preventDefault();
      insertTextAtCursor('**', '**');
    }
    // Ctrl+I: Italic
    if (e.ctrlKey && e.key === 'i') {
      e.preventDefault();
      insertTextAtCursor('*', '*');
    }
    // Tab: Indent
    if (e.key === 'Tab') {
      e.preventDefault();
      insertTextAtCursor('  ');
    }
  };

  // Markdown to HTML preview (simple version)
  const renderPreview = (text: string) => {
    return text
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>')
      .replace(/^- (.*$)/gim, '<li class="ml-4">• $1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4">$1</li>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" class="text-forest-400 underline" target="_blank">$1</a>')
      .replace(/\n/gim, '<br>');
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-neutral-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      {/* Toolbar */}
      <div className="border border-neutral-300 rounded-t-md bg-neutral-50 p-2 flex flex-wrap gap-1">
        {formatButtons.map((button, index) => (
          <button
            key={index}
            type="button"
            onClick={button.action}
            className="px-2 py-1 text-xs bg-white border border-neutral-300 rounded hover:bg-neutral-100 focus:outline-none focus:ring-1 focus:ring-forest-500"
            title={button.name}
          >
            {button.icon}
          </button>
        ))}
        
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-2 py-1 text-xs bg-white border border-neutral-300 rounded hover:bg-neutral-100"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? '📖' : '👁️'}
          </button>
        </div>
      </div>

      {/* Editor Area */}
      <div className={`flex ${isExpanded ? 'h-96' : 'h-auto'}`}>
        {/* Text Input */}
        <div className={isExpanded ? 'w-1/2 pr-2' : 'w-full'}>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`block w-full px-3 py-2 border rounded-b-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500 resize-vertical ${
              error ? 'border-red-300' : 'border-neutral-300'
            } ${isExpanded ? 'h-full resize-none' : 'min-h-[120px]'}`}
            placeholder={placeholder}
            maxLength={maxLength}
          />
        </div>
        
        {/* Preview (expanded mode only) */}
        {isExpanded && (
          <div className="w-1/2 pl-2">
            <div className="h-full p-3 bg-neutral-50 border border-neutral-300 rounded-b-md overflow-y-auto">
              <div className="text-sm text-neutral-600 mb-2">Preview:</div>
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html: renderPreview(value) || '<p class="text-neutral-400 italic">Start typing...</p>'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Error and Help */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      
      <div className="flex justify-between text-xs text-neutral-500">
        <div>
          Markdown supported: **bold**, *italic*, # heading, - list, [link](url)
        </div>
        <div>
          {value.length}/{maxLength} chars
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;